import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CA_CONTENT, ABOUT_CONTENT, HELP_CONTENT, LS_CONTENT } from '@/content/tokenContent';

const PROMPT = 'tux@tuxos:~$ ';

export const Terminal = ({ onOpenWindow }) => {
  const [history, setHistory] = useState([
    { type: 'output', content: 'TUX OS Terminal v1.0.0' },
    { type: 'output', content: 'Type "help" for available commands.' },
    { type: 'output', content: '' },
  ]);
  const [currentInput, setCurrentInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  // Focus input on terminal click
  const handleTerminalClick = () => {
    inputRef.current?.focus();
  };

  const executeCommand = useCallback((cmd) => {
    const trimmedCmd = cmd.trim().toLowerCase();
    const args = trimmedCmd.split(' ');
    const command = args[0];
    const argument = args.slice(1).join(' ').toUpperCase();

    let output = [];

    switch (command) {
      case '':
        break;

      case 'help':
        output = [{ type: 'output', content: HELP_CONTENT }];
        break;

      case 'ls':
        output = [{ type: 'output', content: LS_CONTENT }];
        break;

      case 'clear':
        setHistory([]);
        return;

      case 'cat':
        if (!argument) {
          output = [{ type: 'error', content: 'cat: missing file operand' }];
        } else if (argument === 'CA.TXT') {
          output = [{ type: 'output', content: CA_CONTENT }];
        } else if (argument === 'ABOUT.MD') {
          output = [{ type: 'output', content: ABOUT_CONTENT }];
        } else if (argument === 'TUX-RACER') {
          output = [{ type: 'error', content: 'cat: TUX-RACER: Is a binary file. Use "open TUX-RACER" instead.' }];
        } else if (argument === 'SUPERTUX') {
          output = [{ type: 'error', content: 'cat: SUPERTUX: Is a binary file. Use "open SUPERTUX" instead.' }];
        } else {
          output = [{ type: 'error', content: `cat: ${argument}: No such file or directory` }];
        }
        break;

      case 'open':
        if (!argument) {
          output = [{ type: 'error', content: 'open: missing file operand' }];
        } else if (argument === 'CA.TXT') {
          onOpenWindow('ca');
          output = [{ type: 'success', content: 'Opening CA.txt...' }];
        } else if (argument === 'ABOUT.MD') {
          onOpenWindow('about');
          output = [{ type: 'success', content: 'Opening ABOUT.md...' }];
        } else if (argument === 'TUX-RACER') {
          onOpenWindow('tuxracer');
          output = [{ type: 'success', content: 'Launching TUX-RACER...' }];
        } else if (argument === 'SUPERTUX') {
          onOpenWindow('supertux');
          output = [{ type: 'success', content: 'Launching SUPERTUX...' }];
        } else {
          output = [{ type: 'error', content: `open: ${argument}: No such file or directory` }];
        }
        break;

      case 'whoami':
        output = [{ type: 'output', content: 'tux' }];
        break;

      case 'pwd':
        output = [{ type: 'output', content: '/home/tux' }];
        break;

      case 'uname':
        output = [{ type: 'output', content: 'TUX OS 1.0.0 x86_64 GNU/Linux' }];
        break;

      case 'date':
        output = [{ type: 'output', content: new Date().toString() }];
        break;

      case 'echo':
        output = [{ type: 'output', content: args.slice(1).join(' ') }];
        break;

      case 'cd':
        output = [{ type: 'output', content: '' }];
        break;

      case 'sudo':
        output = [{ type: 'error', content: 'sudo: nice try! 🐧' }];
        break;

      case 'rm':
        output = [{ type: 'error', content: 'rm: operation not permitted (this is a memecoin website, not a real filesystem!)' }];
        break;

      case 'tux':
        output = [{ 
          type: 'output', 
          content: `
        .--.
       |o_o |
       |:_/ |
      //   \\ \\
     (|     | )
    /'\\_   _/\`\\
    \\___)=(___/
    
    🐧 TUX says: HODL!
` 
        }];
        break;

      default:
        output = [{ type: 'error', content: `${command}: command not found. Type "help" for available commands.` }];
    }

    setHistory(prev => [
      ...prev,
      { type: 'input', content: `${PROMPT}${cmd}` },
      ...output
    ]);
  }, [onOpenWindow]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = currentInput;
      
      if (cmd.trim()) {
        setCommandHistory(prev => [...prev, cmd]);
        setHistoryIndex(-1);
      }
      
      executeCommand(cmd);
      setCurrentInput('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      } else {
        setHistoryIndex(-1);
        setCurrentInput('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Basic tab completion for files
      const files = ['CA.txt', 'ABOUT.md', 'TUX-RACER'];
      const words = currentInput.split(' ');
      const lastWord = words[words.length - 1].toUpperCase();
      
      if (lastWord) {
        const match = files.find(f => f.toUpperCase().startsWith(lastWord));
        if (match) {
          words[words.length - 1] = match;
          setCurrentInput(words.join(' '));
        }
      }
    } else if (e.ctrlKey && e.key === 'l') {
      e.preventDefault();
      setHistory([]);
    } else if (e.ctrlKey && e.key === 'c') {
      e.preventDefault();
      setHistory(prev => [...prev, { type: 'input', content: `${PROMPT}${currentInput}^C` }]);
      setCurrentInput('');
    }
  };

  return (
    <div 
      ref={terminalRef}
      className="h-full p-3 overflow-auto font-mono text-sm cursor-text"
      onClick={handleTerminalClick}
      style={{ background: 'hsl(var(--terminal-bg))' }}
    >
      {/* History */}
      {history.map((item, index) => (
        <div 
          key={index} 
          className={`whitespace-pre-wrap break-all leading-relaxed ${
            item.type === 'error' ? 'text-destructive' : 
            item.type === 'success' ? 'text-primary text-glow-subtle' :
            item.type === 'input' ? 'text-primary text-glow-subtle' :
            'text-primary/90'
          }`}
        >
          {item.content}
        </div>
      ))}
      
      {/* Current Input Line */}
      <div className="flex items-start text-primary text-glow-subtle">
        <span className="whitespace-pre">{PROMPT}</span>
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="terminal-input w-full text-primary text-glow-subtle font-mono text-sm bg-transparent"
            autoFocus
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
          />
          {/* Blinking cursor overlay */}
          <span 
            className="absolute pointer-events-none text-primary cursor-blink"
            style={{ left: `${currentInput.length}ch` }}
          >
            █
          </span>
        </div>
      </div>
    </div>
  );
};
